// Lead pipeline and signal labels plus small pure helpers (docs/crm/api.md
// "E5: leads" and "E6: signals"). Kept apart from the screens so wording and
// colors can change in one place.
import { daysBetween, formatDay, humanize } from "./format";

export const OPEN_STAGES = ["spotted", "contacted", "conversation", "proposal"];
export const CLOSED_STAGES = ["won", "lost"];
export const LEAD_STAGES = [...OPEN_STAGES, ...CLOSED_STAGES];

const STAGE_META = {
    spotted: { label: "Spotted", icon: "ri-eye-line" },
    contacted: { label: "Contacted", icon: "ri-send-plane-line" },
    conversation: { label: "Conversation", icon: "ri-discuss-line" },
    proposal: { label: "Proposal", icon: "ri-file-text-line" },
    won: { label: "Won", icon: "ri-trophy-line" },
    lost: { label: "Lost", icon: "ri-close-circle-line" },
};

export const stageLabel = (s) => (STAGE_META[s] ? STAGE_META[s].label : humanize(s));
export const stageIcon = (s) => (STAGE_META[s] ? STAGE_META[s].icon : "ri-funds-line");
export const isOpenStage = (s) => OPEN_STAGES.includes(s);
export const STAGE_OPTIONS = LEAD_STAGES.map((s) => ({ value: s, label: stageLabel(s) }));

export const stageColor = (s) => (s === "won" ? "success" : s === "lost" ? "secondary" : "info");

export const LEAD_SOURCES = [
    { value: "introduction", label: "Introduction", color: "primary" },
    { value: "signal", label: "Signal", color: "info" },
    { value: "direct", label: "Direct", color: "secondary" },
    { value: "inbound", label: "Inbound", color: "success" },
];
const SOURCE_MAP = LEAD_SOURCES.reduce((m, s) => ({ ...m, [s.value]: s }), {});
export const sourceLabel = (v) => (SOURCE_MAP[v] ? SOURCE_MAP[v].label : humanize(v));
export const sourceColor = (v) => (SOURCE_MAP[v] ? SOURCE_MAP[v].color : "secondary");

export const CURRENCIES = ["EUR", "USD", "GBP", "TRY", "AED", "CHF"];

// The board falls back to the company name when a lead has no title.
export const leadName = (l) => (l && (l.title || l.company_name)) || "Lead";

// Stall flag for an open lead: null when there is nothing to flag.
// stalled: the next-step date has passed; no_next_step: step or date missing.
export function stallFlag(lead, today) {
    if (!lead || !lead.is_open) return null;
    if (lead.stall_status === "no_next_step") return { kind: "no_next_step", label: "No next step", color: "warning" };
    if (lead.stall_status === "stalled") {
        const late = lead.next_step_date && today ? daysBetween(lead.next_step_date, today) : null;
        return { kind: "stalled", label: "Stalled", color: "danger", detail: late ? `${late} ${late === 1 ? "day" : "days"} late` : "" };
    }
    return null;
}

// "6d in stage", "Entered stage today", "" for closed leads.
export function daysInStageText(days) {
    if (days === null || days === undefined) return "";
    if (days === 0) return "Entered stage today";
    return `${days}d in stage`;
}

// Whole days as a short duration: 0 -> "< 1 day", 1 -> "1 day", 12 -> "12 days".
export function durationText(days) {
    if (days === null || days === undefined) return "";
    if (days <= 0) return "< 1 day";
    return `${days} ${days === 1 ? "day" : "days"}`;
}

const SYMBOL = { EUR: "€", USD: "$", GBP: "£" };

const num = (n, digits = 2) => Number(n).toLocaleString("en-GB", { maximumFractionDigits: digits });

// formatMoney(48000, "EUR") -> "€48,000"; (1500000, "TRY") -> "TRY 1,500,000".
export function formatMoney(value, currency = "EUR") {
    if (value === null || value === undefined || value === "") return "";
    const cur = (currency || "EUR").toUpperCase();
    return SYMBOL[cur] ? `${SYMBOL[cur]}${num(value)}` : `${cur} ${num(value)}`;
}

// Short EUR amount for column totals: 950 -> "€950", 62500 -> "€62.5k", 1250000 -> "€1.25M".
export function compactEUR(value) {
    const v = Number(value) || 0;
    if (Math.abs(v) < 1000) return `€${num(v, 0)}`;
    if (Math.abs(v) < 1e6) return `€${num(v / 1000, 1)}k`;
    return `€${num(v / 1e6, 2)}M`;
}

// Value line for a lead: main text in its own currency, plus the EUR
// equivalent when the lead is not in EUR. eurUnknown when no rate is set.
export function leadValue(lead) {
    if (!lead || lead.est_value === null || lead.est_value === undefined) return null;
    const cur = (lead.currency || "EUR").toUpperCase();
    const main = formatMoney(lead.est_value, cur);
    if (cur === "EUR") return { main, eur: "", eurUnknown: false };
    if (lead.value_eur === null || lead.value_eur === undefined) return { main, eur: "", eurUnknown: true };
    return { main, eur: `≈ ${formatMoney(lead.value_eur, "EUR")}`, eurUnknown: false };
}

// What a move to `to` asks for: "next_step" for an open stage, "outcome" for
// won/lost, null when it is the current stage.
export function moveNeeds(lead, to) {
    if (!lead || !to || lead.stage === to) return null;
    return isOpenStage(to) ? "next_step" : "outcome";
}

// Clear wording for the API's 422 business-rule codes.
export const RULE_MESSAGES = {
    next_step_required: "An open lead needs a next step and a date for it.",
    outcome_reason_required: "Say why the lead was won or lost.",
};

// { message, field } for an API error; field is "next_step" or
// "outcome_reason" for the 422 codes so the form can mark the input.
export function leadError(err) {
    if (!err) return null;
    const code = err.data && err.data.code;
    if (err.status === 422 && RULE_MESSAGES[code]) {
        return { message: RULE_MESSAGES[code], field: code === "next_step_required" ? "next_step" : "outcome_reason" };
    }
    return { message: err.message || String(err), field: null };
}

// Stage stepper from the lead's stage history (oldest first). One step per
// open stage plus a final Won/Lost (or "Closed" while open). Each step has
// state done | current | skipped | todo, total days spent there, and the
// date it was last entered.
export function buildStepper(stages, lead) {
    const history = stages || [];
    const current = lead ? lead.stage : null;
    const byStage = {};
    history.forEach((h) => {
        const s = byStage[h.to_stage] || { days: 0, enteredAt: null, visits: 0 };
        s.days += h.days || 0;
        s.enteredAt = h.changed_at;
        s.visits += 1;
        byStage[h.to_stage] = s;
    });
    const closed = current && !isOpenStage(current);
    const final = closed ? current : null;
    const order = [...OPEN_STAGES, final || "closed"];
    const currentIdx = closed ? order.length - 1 : order.indexOf(current);
    return order.map((stage, i) => {
        const info = byStage[stage];
        let state = "todo";
        if (i === currentIdx) state = "current";
        else if (info) state = "done";
        else if (i < currentIdx) state = "skipped";
        return {
            stage,
            label: stage === "closed" ? "Won / Lost" : stageLabel(stage),
            state,
            days: info ? info.days : null,
            enteredAt: info ? info.enteredAt : null,
            visits: info ? info.visits : 0,
        };
    });
}

// Next-step date text for cards: "Today", "Tomorrow", "3 days late", "18 Oct".
export function nextStepWhen(ymd, today) {
    if (!ymd) return "";
    const d = daysBetween(today, ymd);
    if (d === null) return "";
    if (d === 0) return "Today";
    if (d === 1) return "Tomorrow";
    if (d < 0) return `${-d} ${d === -1 ? "day" : "days"} late`;
    return formatDay(ymd, { short: true, today });
}

// ---------- signals ----------

export const SIGNAL_TYPES = [
    { value: "funding", label: "Funding", icon: "ri-money-euro-circle-line" },
    { value: "leadership_hire", label: "Hiring a leader", icon: "ri-user-search-line" },
    { value: "leadership_change", label: "Leadership change", icon: "ri-user-shared-line" },
    { value: "layoffs", label: "Layoffs", icon: "ri-user-unfollow-line" },
    { value: "regulatory_event", label: "Regulatory event", icon: "ri-scales-3-line" },
];
const SIGNAL_MAP = SIGNAL_TYPES.reduce((m, s) => ({ ...m, [s.value]: s }), {});
export const signalTypeLabel = (v) => (SIGNAL_MAP[v] ? SIGNAL_MAP[v].label : humanize(v));
export const signalTypeIcon = (v) => (SIGNAL_MAP[v] ? SIGNAL_MAP[v].icon : "ri-radar-line");

// {"funding": 1, "layoffs": 2} -> "2 layoffs · 1 funding" (largest first).
export function typeSummary(types) {
    return Object.keys(types || {})
        .sort((a, b) => types[b] - types[a] || a.localeCompare(b))
        .map((t) => `${types[t]} ${signalTypeLabel(t).toLowerCase()}`)
        .join(" · ");
}

// Host of a source URL for a compact link label.
export function urlHost(url) {
    const m = /^https?:\/\/([^/?#]+)/i.exec(url || "");
    return m ? m[1].replace(/^www\./i, "") : "";
}
